import { Outlet, NavLink, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';
import { MODULE_AREAS } from '../pages/CoreModules';
import { api } from '../lib/api';
import QaCapture from './QaCapture';

/**
 * Left-sidebar Layout. Nav is grouped by purpose so the Cisco/Webex modules
 * sit together separately from platform admin. Sidebar collapses to icons on
 * small screens and is fully collapsible via the toggle button.
 */
interface NavItem { to: string; label: string; exact?: boolean; icon: React.ReactNode; roles?: Me['role'][]; module?: string; tenants?: string[] }
interface NavGroup { label: string; items: NavItem[] }

const I = {
  dash:    <Icon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z" />,
  reports: <Icon d="M3 3v18h18M7 14l4-4 4 4 5-5" />,
  notif:   <Icon d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0" />,
  wxc:     <Icon d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.8a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.35 1.84.59 2.8.72A2 2 0 0 1 22 16.92z" />,
  wxcc:    <Icon d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />,
  cucm:    <Icon d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />,
  uccx:    <Icon d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75 M8.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />,
  cube:    <Icon d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96 12 12.01l8.73-5.05 M12 22.08V12" />,
  // Local Gateway — router/gateway glyph (device box + antenna), distinct from CUBE.
  lgw:     <Icon d="M5 14h14a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2z M7.5 18h.01 M11.5 18h.01 M12 14V7 M9 9.5l3-3 3 3" />,
  spokes:  <Icon d="M12 2v20 M2 12h20 M4.93 4.93l14.14 14.14 M19.07 4.93L4.93 19.07" />,
  config:  <Icon d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />,
  features:<Icon d="M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />,
  support: <Icon d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />,
  qa:      <Icon d="M2 3h20v14H2zM8 21h8 M12 17v4" />,
  tenants: <Icon d="M3 21h18 M3 7v14 M21 7v14 M3 7l9-4 9 4 M9 21V11h6v10" />,
  license: <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M9 15l2 2 4-4" />,
  audit:   <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" />,
  status:  <Icon d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  trouble: <Icon d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />,
  migrate: <Icon d="M5 12h14 M13 6l6 6-6 6 M3 6v12" />,
  convert: <Icon d="M4 7h11 M11 3l4 4-4 4 M20 17H9 M13 13l-4 4 4 4" />,
  signout: <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9" />,
  collapse:<Icon d="M15 18l-6-6 6-6" />,
  expand:  <Icon d="M9 18l6-6-6-6" />,
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operations',
    items: [
      { to: '/',                label: 'Dashboard',       exact: true, icon: I.dash },
      { to: '/reports',         label: 'Reports',                      icon: I.reports },
      { to: '/notifications',   label: 'Notifications',                icon: I.notif },
      { to: '/troubleshooting', label: 'Troubleshooting',              icon: I.trouble },
      { to: '/knowledge-base',  label: 'Knowledge Base',               icon: I.support },
      { to: '/email-reporting', label: 'Email Access',                 icon: I.reports },
      { to: '/call-detail',     label: 'CDR',                          icon: I.reports, tenants: ['santamonica'] },
      { to: '/support',         label: 'Support Tickets',              icon: I.support },
    ],
  },
  {
    label: 'Core Modules',
    items: [
      { to: '/modules/wxc',   label: 'Webex Calling',      icon: I.wxc,  module: 'wxc'  },
      { to: '/modules/wxcc',  label: 'Webex Contact Ctr.', icon: I.wxcc, module: 'wxcc' },
      { to: '/modules/cucm',  label: 'CUCM',               icon: I.cucm, module: 'cucm' },
      { to: '/modules/uccx',  label: 'UCCX',               icon: I.uccx, module: 'uccx' },
      { to: '/modules/lgw',   label: 'Local Gateway',      icon: I.lgw,  module: 'lgw'  },
      { to: '/modules/cube',  label: 'CUBE',               icon: I.cube, module: 'lgw'  },
      { to: '/modules/sip',   label: 'SIP Tools',          icon: I.status },
      { to: '/modules/security', label: 'Security',        icon: I.qa },
    ],
  },
  {
    label: 'Migration',
    items: [
      { to: '/migration',         label: 'WxCC Migration',        icon: I.migrate, module: 'migration' },
      { to: '/migration-tracker', label: 'Migration Tracker',     icon: I.reports, module: 'migration' },
      { to: '/migration-sheet',   label: 'Collaboration Master Tracker', icon: I.reports, module: 'migration' },
      { to: '/uccx-convert',      label: 'UCCX → WxCC Convertor',  icon: I.convert, module: 'uccx_flow_convertor' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/spokes',        label: 'Spokes',        icon: I.spokes },
      { to: '/configuration', label: 'Config Mgr',    icon: I.config },
      { to: '/features',      label: 'Features',      icon: I.features },
      { to: '/qa',            label: 'QA Sentry',     icon: I.qa },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/admin/tenants', label: 'Tenants',  icon: I.tenants },
      { to: '/admin/license', label: 'License',  icon: I.license },
      { to: '/audit',         label: 'Audit',    icon: I.audit },
      { to: '/status',        label: 'Status',   icon: I.status },
    ],
  },
];

type Theme = 'light' | 'dark' | 'system';
function applyTheme(t: Theme) {
  const dark = t === 'dark' || (t === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}
function ThemeToggle({ collapsed }: { collapsed: boolean }) {
  const [theme, setTheme] = useState<Theme>(() => ((typeof localStorage !== 'undefined' && (localStorage.getItem('tnh-theme') as Theme)) || 'system'));
  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem('tnh-theme', theme); } catch {}
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const h = () => applyTheme('system');
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [theme]);
  const opts: { k: Theme; label: string; icon: React.ReactNode }[] = [
    { k: 'light',  label: 'Light',  icon: <Icon d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" /> },
    { k: 'dark',   label: 'Dark',   icon: <Icon d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /> },
    { k: 'system', label: 'Auto',   icon: <Icon d="M3 4h18v12H3zM8 20h8M12 16v4" /> },
  ];
  if (collapsed) {
    const next: Theme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    return (
      <button onClick={() => setTheme(next)} title={`Theme: ${theme} (click to change)`}
        className="flex justify-center w-full px-2 py-1.5 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition">
        {opts.find((o) => o.k === theme)?.icon}
      </button>
    );
  }
  return (
    <div>
      <div className="px-2 mb-1 text-[10px] uppercase tracking-[0.14em] text-gray-400 font-semibold">Theme</div>
      <div className="flex gap-1 px-1">
        {opts.map((o) => (
          <button key={o.k} onClick={() => setTheme(o.k)} title={o.label}
            className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-1 rounded-md text-xs transition ${theme === o.k ? 'bg-tnh-accent/10 text-tnh-accent font-medium' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
            {o.icon}<span>{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Layout({ me }: { me: Me }) {
  const location = useLocation();
  const [sp] = useSearchParams();
  const [unread, setUnread] = useState<number>(0);
  const [enabledModules, setEnabledModules] = useState<Set<string> | null>(null);
  // Always default to expanded — operators complained the collapsed state got
  // stuck via localStorage. Toggle still works during the session but doesn't
  // persist across reloads.
  const [collapsed, setCollapsed] = useState<boolean>(false);
  // Clear any stale persisted collapse so it doesn't pop back on next reload.
  useEffect(() => { try { localStorage.removeItem('tnh-nav-collapsed'); } catch {} }, []);

  // Poll unread count every 30s — the notification-svc is the source of truth.
  useEffect(() => {
    let cancel = false;
    const load = () => api.notificationsUnreadCount().then((r) => { if (!cancel) setUnread(r.unread); }).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Fetch caller's TENANT_MODULES once on mount; refresh every 60s in case
  // subscription-svc bumped the version (downgrade / module toggle).
  useEffect(() => {
    let cancel = false;
    const load = () => api.subscriptionModules()
      .then((r) => { if (!cancel) setEnabledModules(new Set(r.entitlement.modules)); })
      .catch(() => { if (!cancel) setEnabledModules(new Set()); /* fail-closed: show only un-gated items */ });
    load();
    const t = setInterval(load, 60_000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Super-admin sees everything; regular roles see only items whose module is
  // enabled OR has no module gate at all.
  function isItemVisible(item: NavItem): boolean {
    if (item.tenants && !item.tenants.includes(me.tenant_id)) return false;
    if (me.role === 'super_admin') return true;
    if (!item.module) return true;
    if (enabledModules === null) return true;        // pre-fetch: show all (avoids flicker)
    return enabledModules.has(item.module);
  }

  function toggleCollapse() {
    setCollapsed((v) => !v);
  }

  async function handleLogout() {
    try { await api.logout(); }
    catch {
      window.alert('Sign-out request failed — redirecting anyway. On a shared computer, close the browser to clear the session.');
    }
    window.location.replace('/login');
  }

  const sidebarWidth = collapsed ? 'w-16' : 'w-60';

  return (
    <div className="min-h-screen flex bg-tnh-bg dark:bg-tnh-bg-dark text-gray-900 dark:text-gray-100">
      {/* ───── Sidebar ───── */}
      <aside className={`${sidebarWidth} shrink-0 sticky top-0 self-start h-screen border-r border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark flex flex-col transition-all`}>
        {/* Brand */}
        <div className="px-4 py-4 flex items-center gap-2.5 border-b border-tnh-border dark:border-tnh-border-dark">
          <span className="w-8 h-8 rounded-md bg-white grid place-items-center shrink-0 ring-1 ring-tnh-border dark:ring-0">
            <img src="/trinethub-icon.png" alt="TriNetHub" className="w-6 h-6 object-contain" />
          </span>
          {!collapsed && <span className="font-semibold tracking-tight text-base">TriNetHub</span>}
          <button
            onClick={toggleCollapse}
            className="ml-auto p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition shrink-0"
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? I.expand : I.collapse}
          </button>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-3">
          {NAV_GROUPS.map((g) => (
            <div key={g.label} className="mb-4">
              {!collapsed && (
                <div className="px-4 mb-1.5 text-[10px] uppercase tracking-[0.14em] text-gray-400 font-semibold">
                  {g.label}
                </div>
              )}
              <ul className="space-y-0.5 px-2">
                {g.items.filter(isItemVisible).map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      end={n.exact}
                      title={collapsed ? n.label : undefined}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition ${
                          isActive
                            ? 'bg-tnh-accent/10 text-tnh-accent font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        } ${collapsed ? 'justify-center' : ''}`
                      }
                    >
                      <span className="shrink-0">{n.icon}</span>
                      {!collapsed && <span className="truncate">{n.label}</span>}
                      {!collapsed && n.to === '/notifications' && unread > 0 && (
                        <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-tnh-err text-white text-[10px] font-semibold grid place-items-center">
                          {unread > 99 ? '99+' : unread}
                        </span>
                      )}
                    </NavLink>
                    {(() => {
                      const mkey = n.to.startsWith('/modules/') ? n.to.slice('/modules/'.length) : '';
                      const areas = MODULE_AREAS[mkey];
                      if (collapsed || !areas || location.pathname !== n.to) return null;
                      const cur = sp.get('area') || areas[0]?.key;
                      return (
                        <ul className="mt-1 ml-7 mb-1 space-y-0.5 border-l border-tnh-border dark:border-tnh-border-dark">
                          {areas.map((a) => (
                            <li key={a.key}>
                              <NavLink
                                to={`${n.to}?area=${a.key}`}
                                className={`block pl-3 pr-2 py-1 rounded-md text-[13px] transition ${
                                  cur === a.key
                                    ? 'bg-tnh-accent/10 text-tnh-accent font-medium'
                                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                              >
                                {a.label}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Bottom: theme + user + signout */}
        <div className="border-t border-tnh-border dark:border-tnh-border-dark px-2 py-3 space-y-1.5">
          <ThemeToggle collapsed={collapsed} />
          {!collapsed && (
            <div className="px-2 pb-1 pt-1 border-t border-tnh-border/60 dark:border-tnh-border-dark/60">
              <div className="text-xs font-medium truncate">{me.email}</div>
              <div className="text-[11px] text-gray-500">
                {me.role.replace('_', ' ')} · {me.tenant_id}
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            title={collapsed ? 'Sign out' : undefined}
            className={`flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition ${collapsed ? 'justify-center' : ''}`}
          >
            {I.signout}
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* ───── Main column ───── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Slim top bar — page chrome + unread bell + tenant chip on mobile */}
        <header className="h-12 px-6 border-b border-tnh-border dark:border-tnh-border-dark bg-tnh-card/60 dark:bg-tnh-card-dark/60 backdrop-blur flex items-center justify-end gap-3">
          <Link to="/notifications" className="relative p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition" title="Notifications">
            {I.notif}
            {unread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-tnh-err text-white text-[10px] font-semibold grid place-items-center">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Link>
        </header>

        <main className="flex-1 min-w-0">
          <div className="w-full px-6 py-6">
            <Outlet />
          </div>
        </main>

        <footer className="border-t border-tnh-border dark:border-tnh-border-dark py-3 text-xs text-gray-500 text-center">
          TriNetHub · v1.1.0 · Cisco/Webex operations hub
        </footer>
      </div>

      {/* TriNet AI Assistance — floating assistant on every page */}
      <TriNetAiAssistant me={me} />
      <QaCapture />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TriNet AI Assistance — floating chat assistant, present on every page.
// Talks to ai-svc (/api/ai/chat) through the gateway (Cloudflare Workers AI).
// Agentic "run/deploy/automate" routes through the Autopilot + Cowork bridge
// (phase 2); today it assists, explains, and drafts.
// ---------------------------------------------------------------------------
const AI_API = (import.meta as any).env?.VITE_API_BASE ?? 'https://api.trinethub.com';
type ChatMsg = { role: 'user' | 'assistant'; content: string };

function TriNetAiAssistant({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([{ role: 'assistant', content: `Hi ${me.email.split('@')[0]} — I'm TriNet AI Assistance. Ask me anything about your Cisco/Webex estate, migrations, reports, or the platform. To run or automate something, tell me what you want and I'll lay out the steps.` }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...msgs, { role: 'user' as const, content: text }];
    setMsgs(next); setInput(''); setBusy(true);
    try {
      const r = await fetch(`${AI_API}/api/ai/chat`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next, page: window.location.pathname }),
      });
      const j = await r.json();
      setMsgs((m) => [...m, { role: 'assistant', content: j.ok ? j.reply : `⚠️ ${j.error || 'AI unavailable'}` }]);
    } catch (e: any) {
      setMsgs((m) => [...m, { role: 'assistant', content: `⚠️ ${e.message}` }]);
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} title="TriNet AI Assistance"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full bg-tnh-accent text-white shadow-lg hover:brightness-110 transition">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a2 2 0 0 1 2 2v1h3a3 3 0 0 1 3 3v2a3 3 0 0 1 0 6v0a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v0a3 3 0 0 1 0-6V8a3 3 0 0 1 3-3h3V4a2 2 0 0 1 2-2z" /><path d="M9 13h.01M15 13h.01" /></svg>
        <span className="text-sm font-semibold">TriNet AI</span>
      </button>
    );
  }
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[min(420px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-3rem))] flex flex-col rounded-2xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark shadow-2xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-tnh-border dark:border-tnh-border-dark">
        <span className="w-7 h-7 rounded-md bg-tnh-accent grid place-items-center text-white text-xs font-bold">AI</span>
        <div className="leading-tight">
          <div className="text-sm font-semibold">TriNet AI Assistance</div>
          <div className="text-[10px] text-gray-400">Cloudflare · tenant {me.tenant_id}</div>
        </div>
        <button onClick={() => setOpen(false)} className="ml-auto text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-tnh-accent text-white rounded-br-sm' : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-bl-sm'}`}>{m.content}</div>
          </div>
        ))}
        {busy && <div className="text-xs text-gray-400 px-1 animate-pulse">TriNet AI is thinking…</div>}
      </div>
      <div className="p-3 border-t border-tnh-border dark:border-tnh-border-dark flex gap-2">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask TriNet AI…  (Enter to send)"
          className="flex-1 resize-none px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm focus:outline-none focus:ring-1 focus:ring-tnh-accent" />
        <button onClick={send} disabled={busy || !input.trim()} className="px-3 py-2 rounded-lg bg-tnh-accent text-white text-sm font-medium disabled:opacity-50">Send</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compact stroke-icon component. All icons share a 24×24 viewbox + 2px stroke
// so the sidebar has a consistent visual rhythm. `d` accepts space-separated
// paths (split into <path> elements).
// ---------------------------------------------------------------------------
function Icon({ d }: { d: string }) {
  const paths = d.split(/\s+M/).map((seg, i) => (i === 0 ? seg : `M${seg}`));
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {paths.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}
